<template>
  <div class="h-full lg:h-[86rem]">
    <HeaderMenuModalLinkButton
      link-url="https://www.halebop.se/mitt-halebop"
      icon="log-in"
      heading="Logga in"
      text="Kolla din surf, fakturor och sånt"
    />
    <HeaderMenuModalLinkButton
      link-url="https://www.halebop.se/erbjudanden"
      icon="gift"
      heading="Månadensdeal"
      text="Se aktiva erbjudanden"
      background-class="bg-surface-highlight"
    />
    <AppAccordion
      v-for="link in accordionLinks"
      :key="link.label"
      :base-html-id="link.label"
      :is-open="isAccordionOpen(link.label, link.isOpenByDefault === true)"
      border-color="secondary"
      @on-toggle="closeAllOther($event.isOpen, $event.id)"
    >
      <template #header>
        <p class="heading-xxs">{{ link.label }}</p>
      </template>
      <ul class="pb-16 ml-16 list-none">
        <li
          v-for="childLink in link.children"
          :key="childLink.label + childLink.url"
          class="hover:bg-surface-action-hover"
        >
          <NuxtLink
            class="no-underline text-text-primary w-full block py-16"
            :to="childLink.url"
            external
            :aria-label="childLink.aria"
            @click="childLink.url && pushSubMenyClickEvent(childLink.url)"
          >
            <span class="link-focus">{{ childLink.label }}</span>
          </NuxtLink>
        </li>
      </ul>
    </AppAccordion>

    <div
      v-for="link in links"
      :key="link.label + link.url + link.children?.length"
    >
      <HeaderMenuModalLinkItem :link="link" :is-open="!!link.isOpenByDefault" />
    </div>

    <HeaderMenuModalLinkButton
      link-url="https://www.halebop.se/support"
      icon="chat"
      heading="Support"
      text="Chattsupport dygnet runt"
    />
    <div class="py-16"></div>
  </div>
</template>

<script lang="ts" setup>
export interface MenuModalLink {
  label: string;
  isOpenByDefault?: boolean | false;
  url?: string;
  aria?: string;
  children?: MenuModalLink[];
}

interface Props {
  menuIsShownVisually: boolean;
}
const props = defineProps<Props>();

const { pushSubMenyClickEvent } = useGoogleAnalyticsEvents();
const route = useRoute();

const openAccordion: Ref<string> = ref('');
const allowAccordionsToBeOpened = ref<boolean>(false);

const closeAllOther = async (isOpen: boolean, id: string) => {
  if (isOpen) {
    openAccordion.value = id;
  }
};

const isAccordionOpen = (
  accordionLinkLabel: string,
  accordionShouldBeOpenByDefault: boolean,
): boolean => {
  if (!allowAccordionsToBeOpened.value) {
    return false;
  }

  if (openAccordion.value) {
    return openAccordion.value.includes(accordionLinkLabel);
  }

  return accordionShouldBeOpenByDefault;
};

const abonnemangAccordionShouldBeOpenByDefault = [
  '/mobilabonnemang',
  '/familjeabonnemang',
  '/student',
].includes(route.path);

const accordionLinks: MenuModalLink[] = [
  {
    label: 'Mobiler',
    isOpenByDefault: !abonnemangAccordionShouldBeOpenByDefault,
    children: [
      {
        label: 'Alla',
        url: '/mobiltelefoner',
        aria: 'Mobiltelefoner',
      },
      {
        label: 'Begagnat',
        url: '/begagnat',
        aria: 'Begagnade mobiler',
      },
      {
        label: 'Dumpa din lur',
        url: 'https://www.halebop.se/dumpa-din-lur',
      },
    ],
  },
  {
    label: 'Abonnemang',
    isOpenByDefault: abonnemangAccordionShouldBeOpenByDefault,
    children: [
      {
        label: 'Ordinarie',
        url: '/mobilabonnemang',
        aria: 'Abonnemang',
      },
      {
        label: 'Familj',
        url: '/familjeabonnemang',
        aria: 'Familjeabonnemang',
      },
      {
        label: 'Student',
        url: '/student',
        aria: 'Studentabonnemang',
      },
    ],
  },
];

const links: MenuModalLink[] = [
  {
    label: 'Bredband',
    url: 'https://www.halebop.se/bredband',
  },
  {
    label: 'Mitt Halebop',
    url: 'https://www.halebop.se/mitt-halebop',
  },
  {
    label: 'Kontantkort',
    url: '/kontantkort',
  },
  {
    label: 'Ladda',
    url: 'https://www.halebop.se/ladda',
  },
  {
    label: 'Tillbehör',
    url: '/tillbehor',
  },
  {
    label: 'Tjänster',
    url: 'https://www.halebop.se/tjanster',
  },
  {
    label: 'Begagnat',
    url: '/begagnat',
  },
  {
    label: 'Om Halebop',
    url: 'https://www.halebop.se/om-halebop',
  },
];

// Keep track of a ref that says if accordions in the menu
// are allowed to be opened programmatically, because when
// the modal is hidden but still rendered in the html DOM
// the accordions can't calculate their html height
watch(
  () => props.menuIsShownVisually,
  async (isShown) => {
    if (isShown) {
      await nextTick();
      allowAccordionsToBeOpened.value = true;
    } else {
      allowAccordionsToBeOpened.value = false;
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
a:focus {
  outline: none;
}
a:focus > .link-focus {
  @apply border-md border-solid border-border-highlight rounded-xs;
}
.router-link-active {
  @apply text-icon-link;
  &::after {
    content: '';
    display: inline-block;
    height: 0.6rem;
    width: 0.6rem;
    border-radius: 50%;
    margin: 2px 6px;
    vertical-align: middle;
    @apply bg-icon-link;
  }
}
</style>
